<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";

import BaseTextarea from "./BaseTextarea.vue";

defineOptions({ inheritAttrs: false });

const model = defineModel<string>({ required: true });

const props = withDefaults(
  defineProps<{
    autofocus?: boolean;
    summaryMaxLength?: number;
    expandDescription?: boolean;
    readonly?: boolean;
  }>(),
  { summaryMaxLength: 90 },
);

const expanded = ref(false);
const textareaRef = useTemplateRef("textarea");
const summaryRef = useTemplateRef("summary");

onMounted(initializeExpandedState);

watch(
  () => props.expandDescription,
  (expand) => (expanded.value = expand),
  { immediate: true },
);

const shouldShowSummary = computed(
  () => model.value !== "" && !expanded.value && (!props.expandDescription || showButtons.value),
);
const summaryText = computed(() => model.value.split("\n")[0]);
const showButtons = computed(() => {
  const totalLength = model.value.length;
  const hasNewLines = model.value.includes("\n");
  return (totalLength > props.summaryMaxLength || hasNewLines) && props.summaryMaxLength > 0;
});

function toggleExpand() {
  expanded.value = !expanded.value;

  // Ensure focus moves to the correct element
  // (textarea when expanded, summary when collapsed)
  setTimeout(() => {
    const targetEl = expanded.value ? textareaRef : summaryRef;
    targetEl.value?.focus();
  }, 150 + 50); // Timeout should exceed faster-fade transition duration (150ms)
}

function onFocus() {
  expanded.value = true;
}

function initializeExpandedState() {
  if (model.value === "") {
    expanded.value = true;
  }
}
</script>

<template>
  <div>
    <Transition name="faster-fade" mode="out-in">
      <button
        v-if="shouldShowSummary"
        ref="summary"
        class="description-summary"
        :aria-label="$t('label.objective.summaryButton', { summary: summaryText })"
        @click="toggleExpand"
      >
        <span class="description-summary-text">{{ summaryText }}</span>
        <span v-if="showButtons" class="see-more-link">
          {{ $t("textarea.seeMore") }}
        </span>
      </button>
      <div v-else>
        <BaseTextarea
          ref="textarea"
          v-bind="$attrs"
          v-model="model"
          class="description-textarea"
          :readonly
          :placeholder="$t('objectives.description.placeholder')"
          :class="{ expanded }"
          @focus="onFocus"
        />
        <button v-if="showButtons" class="see-less-link" @click="toggleExpand">
          <span>{{ $t("textarea.seeLess") }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";
@use "@/styles/mixins/typography";

.description-summary {
  padding: 0.5rem;
  color: colors.$text;
  display: flex;
  align-items: center;
  max-width: 100%;
  outline: revert;
  cursor: pointer;

  &-text {
    @include utils.ellipsis;

    flex: 1;
  }
}

.see-more-link,
.see-less-link {
  @include typography.small("bold");

  cursor: pointer;
  color: colors.$text;
  outline: revert;
  padding-left: 0.5rem;
}
</style>
